import {
  Component,
  inject,
  ElementRef,
  ViewChild,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { from, map, of, switchMap } from 'rxjs';
import { NavLink, navLinks } from '@app/core/models/data';
import { AuthService } from '@app/core/services/auth.service';
import { ApiService } from '@app/core/services/api.service';
import { CollaboratorService } from '@app/core/services/collaborator.service';

@Component({
  selector: 'app-footer',
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatDividerModule,
    MatSnackBarModule,
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css',
})
export class FooterComponent implements OnDestroy {
  navLinks: NavLink[] = navLinks;
  currentYear = new Date().getFullYear();
  private auth = inject(AuthService);
  private api = inject(ApiService);
  private collaboratorsSvc = inject(CollaboratorService);
  private platformId = inject(PLATFORM_ID);
  user$ = this.auth.userDoc$();

  /**
   * Whether the CURRENT user is an active Execution Partner — drives the
   * "Partner Dashboard" footer link. Deliberately separate from `user$`
   * (which is the Firestore user-profile doc, used only for the
   * logged-in/guest check): this resolves against the Collaborator
   * record via the same resolvePartnerAccess() used everywhere else a
   * partner needs to be identified (project-new.component.ts,
   * home-new.component.ts), so "partner" means the same thing site-wide.
   *
   * Emits false for guests, and for logged-in users who are a pending,
   * paused, or offboarded collaborator — only an 'active' record counts,
   * same rule as the workspace header's Partner Dashboard button.
   *
   * ASSUMPTION: uses `this.auth.user$` (the raw Firebase Auth User
   * observable) — matches the pattern already used in
   * project-new.component.ts / home-new.component.ts. If AuthService
   * exposes this under a different name, this is a one-line fix.
   */
  isPartner$ = this.auth.user$.pipe(
    switchMap((user) =>
      user
        ? from(this.collaboratorsSvc.resolvePartnerAccess(user.uid, user.email))
        : of(null),
    ),
    map((collaborator) => !!collaborator && collaborator.status === 'active'),
  );

  restrictedLabels = ['Product Support', 'Feedback'];
  comingSoonLabels = ['Partner with us', 'News', 'Learn AI'];
  /** Labels that require an ACTIVE partner record, not just being logged
   *  in — a stricter gate than restrictedLabels. See isPartnerOnly(). */
  partnerOnlyLabels = ['Partner Dashboard'];

  isRestricted(label: string, isLoggedIn: boolean): boolean {
    return !isLoggedIn && this.restrictedLabels.includes(label);
  }

  isComingSoon(label: string): boolean {
    return this.comingSoonLabels.includes(label);
  }

  /** True for links (currently just "Partner Dashboard") that should only
   *  be navigable by an approved, active Execution Partner. */
  isPartnerOnly(label: string): boolean {
    return this.partnerOnlyLabels.includes(label);
  }

  handleLinkClick(event: MouseEvent, item: any, isLoggedIn: boolean, isPartner: boolean) {
    if (item.externalUrl) {
      return;
    }
    if (this.isComingSoon(item.label)) {
      event.preventDefault();
      this.api['snack'].showError(item.label + ' is coming soon!');
      return;
    }
    if (this.isPartnerOnly(item.label)) {
      if (!isLoggedIn) {
        event.preventDefault();
        this.api['snack'].showError('Please login to access ' + item.label);
        return;
      }
      if (!isPartner) {
        event.preventDefault();
        this.api['snack'].showError(
          'Apply to become an Execution Partner to unlock your dashboard.',
        );
        return;
      }
      // Logged in AND an active partner — let the routerLink navigate normally.
      return;
    }
    if (this.isRestricted(item.label, isLoggedIn)) {
      event.preventDefault();
      this.api['snack'].showError('Please login to access ' + item.label);
    }
  }

  // ── NEW: cursor-following ember glow + spark effects ──────────────

  @ViewChild('footerSection', { static: true }) footerSectionRef!: ElementRef<HTMLElement>;
  @ViewChild('footerGlow', { static: true }) footerGlowRef!: ElementRef<HTMLElement>;

  private hasHoverPointer = false;
  private targetX = 0;
  private targetY = 0;
  private currentX = 0;
  private currentY = 0;
  private lastSparkTime = 0;
  private glowAnimationFrame?: number;

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.hasHoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.hasHoverPointer) {
        this.animateGlow();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.glowAnimationFrame) {
      cancelAnimationFrame(this.glowAnimationFrame);
    }
  }

  onFooterMouseMove(event: MouseEvent): void {
    if (!this.hasHoverPointer) return;
    const rect = this.footerSectionRef.nativeElement.getBoundingClientRect();
    this.targetX = event.clientX - rect.left;
    this.targetY = event.clientY - rect.top;

    const now = performance.now();
    if (now - this.lastSparkTime > 220 && Math.random() > 0.5) {
      const offsetX = (Math.random() - 0.5) * 20;
      this.spawnSpark(this.targetX + offsetX, this.targetY, 0, -40);
      this.lastSparkTime = now;
    }
  }

  onFooterClick(event: MouseEvent): void {
    const rect = this.footerSectionRef.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const count = 14;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const distance = 30 + Math.random() * 40;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 15;
      this.spawnSpark(x, y, dx, dy, 3 + Math.random() * 2);
    }
  }

  sparks: { id: number; x: number; y: number; dx: number; dy: number; size: number }[] = [];
  private sparkIdCounter = 0;

  private spawnSpark(x: number, y: number, dx: number, dy: number, size = 3): void {
    const id = this.sparkIdCounter++;
    this.sparks.push({ id, x, y, dx, dy, size });
    setTimeout(() => {
      this.sparks = this.sparks.filter((s) => s.id !== id);
    }, 1100);
  }

  trackSparkById(index: number, spark: { id: number }): number {
    return spark.id;
  }

  getSparkStyle(spark: { x: number; y: number; dx: number; dy: number; size: number }): Record<string, string> {
    return {
      left: spark.x + 'px',
      top: spark.y + 'px',
      width: spark.size + 'px',
      height: spark.size + 'px',
      '--dx': spark.dx + 'px',
      '--dy': spark.dy + 'px',
    };
  }

  private animateGlow = (): void => {
    this.currentX += (this.targetX - this.currentX) * 0.12;
    this.currentY += (this.targetY - this.currentY) * 0.12;

    const glowEl = this.footerGlowRef.nativeElement;
    glowEl.style.left = this.currentX + 'px';
    glowEl.style.top = this.currentY + 'px';

    this.glowAnimationFrame = requestAnimationFrame(this.animateGlow);
  };
}